/* ================= IMPORT ================= */
import { Card, Row, Col } from "react-bootstrap";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip
} from "recharts";
import { useTranslate } from "../../hooks/LanguageProvider";

/* ================= TYPE ================= */
interface ReadinessItem {
  name: string;
  value: number;
}

interface SupplierReadinessCardProps {
  readiness?: ReadinessItem[];
}

/* ================= COLORS ================= */
const COLORS = ["#16a34a", "#f59e0b", "#dc2626"];
const LABELS: Record<string, string> = {
  "OEM Ready": "Homologues / prets OEM",
  "Upgrade Required": "A qualifier",
  Blocked: "Bloques",
};

const defaultReadiness: ReadinessItem[] = [
  { name: "OEM Ready", value: 0 },
  { name: "Upgrade Required", value: 0 },
  { name: "Blocked", value: 0 }
];

/* ================= CUSTOM LABEL ================= */
const RADIAN = Math.PI / 180;

const renderCustomizedLabel = (props: any) => {
  const {
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent
  } = props;

  if (!percent || percent < 0.05) return null;

  const radius = innerRadius + (outerRadius - innerRadius) / 2;

  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="#fff"
      textAnchor="middle"
      dominantBaseline="central"
      style={{ fontSize: "12px", fontWeight: "bold" }}
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

/* ================= COMPONENT ================= */
export default function SupplierReadinessCard({
  readiness = defaultReadiness
}: SupplierReadinessCardProps) {
  const { translate } = useTranslate();
  const source = readiness.length ? readiness : defaultReadiness;
  const cleanReadiness = defaultReadiness.map((item) => {
    const found = source.find((row) => row.name === item.name);
    return { ...item, value: Number(found?.value || 0) };
  });

  const total = cleanReadiness.reduce(
    (sum, item) => sum + Number(item.value || 0),
    0
  );

  const getPercent = (value: number) => {
    if (!total) return 0;
    return Math.round((Number(value || 0) / total) * 100);
  };

  const globalScore = getPercent(cleanReadiness[0]?.value || 0);
  const hasData = total > 0;

  return (
    <Card className="border-0 shadow-sm rounded-4 h-100">
      <Card.Body>

        {/* HEADER */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="fw-semibold mb-0">{translate("Supplier Readiness")}</h6>
          <small className="text-muted">{total} {translate("fournisseur(s)")}</small>
        </div>

        <Row>

          {/* CHART */}
          <Col xs={6}>
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                {hasData ? (
                  <Pie
                    data={cleanReadiness}
                    dataKey="value"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={3}
                    label={renderCustomizedLabel}
                  >
                    {cleanReadiness.map((entry, index) => (
                      <Cell key={index} fill={COLORS[index] || "#64748b"} />
                    ))}
                  </Pie>
                ) : (
                  <Pie
                    data={[{ name: translate("Aucune donnee"), value: 1 }]}
                    dataKey="value"
                    innerRadius={50}
                    outerRadius={80}
                  >
                    <Cell fill="#e5e7eb" />
                  </Pie>
                )}
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Col>

          {/* STATS */}
          <Col xs={6} className="d-flex flex-column justify-content-center">

            {cleanReadiness.map((item, index) => (
              <div key={index} className="mb-2">

                <small className="text-muted">
                  {translate(LABELS[item.name] || item.name)}
                </small>

                <div
                  className="fw-bold"
                  style={{ color: COLORS[index] || "#64748b" }}
                >
                  {item.value} <span className="small">({getPercent(item.value)}%)</span>
                </div>

              </div>
            ))}

          </Col>

        </Row>

        {/* PROGRESS GLOBAL */}
        <div className="mt-3">

          <div className="d-flex justify-content-between">
            <small className="text-muted">
              {translate("Global Industrial Readiness")}
            </small>
            <small className="fw-bold text-success">
              {globalScore}%
            </small>
          </div>

          <div
            className="progress mt-1"
            style={{ height: "8px" }}
          >
            <div
              className="progress-bar bg-success"
              style={{ width: `${globalScore}%` }}
            ></div>
          </div>

        </div>

      </Card.Body>
    </Card>
  );
}
